import React from 'react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';

import { Users, Shield, FileText, GraduationCap, Archive, RefreshCw, Edit, Trash2, Search, ChevronLeft, ChevronRight, CheckCircle, Clock, CreditCard, History, Plus, Settings, Crown, FolderOpen, BarChart3 } from 'lucide-react';
import BackButton from '../components/BackButton';

import { StatsCard, DataTable, EditModal } from './superAdminCMSHelpers';
import { useSuperAdminCMS } from './useSuperAdminCMS';

export default function SuperAdminCMSPage() {
  const {
    activeTab, auditTrail, data, editItem, getColumns, handleArchive,
    handleBulkAction, handleDelete, handleRestore, handleSaveEdit, loadData, loading,
    navigate, pagination, saving, search, selectedIds, setActiveTab,
    setEditItem, setSearch, setSelectedIds, setShowArchived, showArchived, stats,
    tabs,
  } = useSuperAdminCMS();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BackButton to="/admin/dashboard" label="Dashboard" className="text-white hover:bg-white/10" />
              <div>
                <div className="flex items-center gap-2">
                  <Crown className="w-6 h-6 text-yellow-400" />
                  <h1 className="text-2xl font-bold">Super Admin CMS</h1>
                </div>
                <p className="text-purple-200 text-sm">Gestion complète de tous les éléments du site</p>
              </div>
            </div>
            <Button 
              onClick={() => loadData()} 
              variant="outline" 
              className="border-white/30 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation tabs */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex overflow-x-auto gap-1 py-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-purple-700 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Dashboard */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatsCard
                title="Membres"
                value={stats.membres?.total || 0}
                icon={Users}
                color="blue"
                subStats={[
                  { icon: <CheckCircle className="w-3 h-3 text-green-500" />, label: 'Actifs', value: stats.membres?.actifs || 0 },
                  { icon: <Clock className="w-3 h-3 text-yellow-500" />, label: 'En attente', value: stats.membres?.en_attente || 0 },
                  { icon: <Archive className="w-3 h-3 text-orange-500" />, label: 'Archivés', value: stats.membres?.archives || 0 }
                ]}
              />
              <StatsCard
                title="COOPER'S"
                value={stats.coopers?.total || 0}
                icon={Shield}
                color="amber"
                subStats={[
                  { icon: <CheckCircle className="w-3 h-3 text-green-500" />, label: 'Validés', value: stats.coopers?.valides || 0 },
                  { icon: <Clock className="w-3 h-3 text-yellow-500" />, label: 'En attente', value: stats.coopers?.en_attente || 0 },
                  { icon: <Archive className="w-3 h-3 text-orange-500" />, label: 'Archivés', value: stats.coopers?.archives || 0 }
                ]}
              />
              <StatsCard
                title="Souscriptions"
                value={stats.souscriptions?.total || 0}
                icon={FileText}
                color="green"
                subStats={[
                  { icon: <CheckCircle className="w-3 h-3 text-green-500" />, label: 'Actives', value: stats.souscriptions?.actives || 0 },
                  { icon: <Clock className="w-3 h-3 text-yellow-500" />, label: 'En attente', value: stats.souscriptions?.en_attente || 0 },
                  { icon: <Archive className="w-3 h-3 text-orange-500" />, label: 'Archivées', value: stats.souscriptions?.archives || 0 }
                ]}
              />
              <StatsCard
                title="Formations"
                value={stats.formations?.modules || 0}
                icon={GraduationCap}
                color="purple"
                subStats={[
                  { icon: <CreditCard className="w-3 h-3 text-blue-500" />, label: 'Packs', value: stats.formations?.packs || 0 },
                  { icon: <Users className="w-3 h-3 text-green-500" />, label: 'Inscriptions', value: stats.formations?.inscriptions || 0 }
                ]}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('membres')}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold">Gérer les Membres</p>
                      <p className="text-sm text-gray-500">Modifier, archiver, restaurer</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('coopers')}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-amber-100 rounded-lg">
                      <Shield className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold">Gérer les COOPER&apos;S</p>
                      <p className="text-sm text-gray-500">Validation, modification, archivage</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('souscriptions')}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <FileText className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold">Gérer les Souscriptions</p>
                      <p className="text-sm text-gray-500">Suivi et gestion complète</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Liens rapides vers autres CMS */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Autres modules de gestion</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Button variant="outline" onClick={() => navigate('/admin/pages-management')} className="justify-start">
                    <FileText className="w-4 h-4 mr-2" />
                    Pages Statiques
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/cms')} className="justify-start">
                    <Settings className="w-4 h-4 mr-2" />
                    Contenu CMS
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/dashboard')} className="justify-start">
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Nomenclatures
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/dashboard')} className="justify-start">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Statistiques
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Data Tables */}
        {activeTab !== 'dashboard' && activeTab !== 'audit' && (
          <div className="space-y-4">
            {/* Toolbar */}
            <Card>
              <CardContent className="py-4">
                <div className="flex flex-wrap items-center gap-4">
                  {/* Search */}
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Rechercher..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && loadData()}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  {/* Filters */}
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={showArchived}
                      onChange={(e) => setShowArchived(e.target.checked)}
                      className="rounded"
                    />
                    Inclure archivés
                  </label>

                  {/* Bulk actions */}
                  {selectedIds.length > 0 && (
                    <div className="flex items-center gap-2 border-l pl-4">
                      <span className="text-sm text-gray-600">{selectedIds.length} sélectionné(s)</span>
                      <Button size="sm" variant="outline" onClick={() => handleBulkAction('validate')} className="text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Valider
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleBulkAction('archive')} className="text-orange-600">
                        <Archive className="w-4 h-4 mr-1" />
                        Archiver
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleBulkAction('delete')} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{tabs.find(t => t.id === activeTab)?.label} ({pagination.total})</span>
                  {(activeTab === 'formations' || activeTab === 'packs') && (
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                  </div>
                ) : (
                  <>
                    <DataTable
                      data={data}
                      columns={getColumns()}
                      onEdit={setEditItem}
                      onArchive={handleArchive}
                      onRestore={handleRestore}
                      onDelete={handleDelete}
                      selectedIds={selectedIds}
                      setSelectedIds={setSelectedIds}
                      showArchived={showArchived}
                    />

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <span className="text-sm text-gray-600">
                          Page {pagination.page} sur {pagination.pages}
                        </span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={pagination.page === 1}
                            onClick={() => loadData(activeTab, pagination.page - 1)}
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={pagination.page === pagination.pages}
                            onClick={() => loadData(activeTab, pagination.page + 1)}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Audit Trail */}
        {activeTab === 'audit' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historique des modifications
              </CardTitle>
              <CardDescription>
                Toutes les actions effectuées par les administrateurs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                </div>
              ) : (
                <div className="space-y-3">
                  {auditTrail.length === 0 ? (
                    <p className="text-center text-gray-500 py-4 md:py-8">Aucune action enregistrée</p>
                  ) : (
                    auditTrail.map((audit) => (
                      <div key={audit.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                        <div className={`p-2 rounded-lg ${
                          audit.action?.includes('delete') ? 'bg-red-100' :
                          audit.action?.includes('archive') ? 'bg-orange-100' :
                          audit.action?.includes('create') ? 'bg-green-100' :
                          'bg-blue-100'
                        }`}>
                          {audit.action?.includes('delete') ? <Trash2 className="w-4 h-4 text-red-600" /> :
                           audit.action?.includes('archive') ? <Archive className="w-4 h-4 text-orange-600" /> :
                           audit.action?.includes('create') ? <Plus className="w-4 h-4 text-green-600" /> :
                           <Edit className="w-4 h-4 text-blue-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">
                            {audit.action} - {audit.entity_type}
                          </p>
                          <p className="text-sm text-gray-600">
                            Par {audit.performed_by}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(audit.performed_at).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Modal */}
      <EditModal
        isOpen={!!editItem}
        onClose={() => setEditItem(null)}
        title={`Modifier ${activeTab === 'membres' ? 'le membre' : activeTab === 'coopers' ? 'le COOPER\'S' : 'l\'élément'}`}
        onSave={handleSaveEdit}
        saving={saving}
      >
        {editItem && (
          <div className="space-y-4">
            {activeTab === 'membres' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                    <input
                      type="text"
                      value={editItem.nom || ''}
                      onChange={(e) => setEditItem({ ...editItem, nom: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                    <input
                      type="text"
                      value={editItem.prenom || ''}
                      onChange={(e) => setEditItem({ ...editItem, prenom: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editItem.email || ''}
                    onChange={(e) => setEditItem({ ...editItem, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={editItem.status || editItem.statut || ''}
                    onChange={(e) => setEditItem({ ...editItem, status: e.target.value, statut: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="en_attente_connexion">En attente</option>
                    <option value="actif">Actif</option>
                    <option value="incomplete">Incomplet</option>
                    <option value="suspendu">Suspendu</option>
                    <option value="inactif">Inactif</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise</label>
                  <input
                    type="text"
                    value={editItem.entreprise || ''}
                    onChange={(e) => setEditItem({ ...editItem, entreprise: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </>
            )}
            {activeTab === 'coopers' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Raison sociale</label>
                  <input
                    type="text"
                    value={editItem.raison_sociale || ''}
                    onChange={(e) => setEditItem({ ...editItem, raison_sociale: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editItem.email || ''}
                    onChange={(e) => setEditItem({ ...editItem, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SIRET</label>
                  <input
                    type="text"
                    value={editItem.siret || ''}
                    onChange={(e) => setEditItem({ ...editItem, siret: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={editItem.statut || ''}
                    onChange={(e) => setEditItem({ ...editItem, statut: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="en_attente">En attente</option>
                    <option value="actif">Actif</option>
                    <option value="suspendu">Suspendu</option>
                    <option value="rejete">Rejeté</option>
                  </select>
                </div>
              </>
            )}
            {activeTab === 'souscriptions' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Réf.Solidaire</label>
                  <input
                    type="text"
                    value={editItem.numero_police || ''}
                    onChange={(e) => setEditItem({ ...editItem, numero_police: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={editItem.statut || ''}
                    onChange={(e) => setEditItem({ ...editItem, statut: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="brouillon">Brouillon</option>
                    <option value="en_attente_paiement">En attente paiement</option>
                    <option value="active">Active</option>
                    <option value="resiliee">Résiliée</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cotisation annuelle (€)</label>
                  <input
                    type="number"
                    value={editItem.cotisation_annuelle || 0}
                    onChange={(e) => setEditItem({ ...editItem, cotisation_annuelle: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </>
            )}
          </div>
        )}
      </EditModal>
    </div>
  );
}
